import React, { useState, useEffect } from 'react';
import { moFamilyStore } from '../stores/moFamilyStore';
import { SlotSet } from '../types/slot-system';

interface SlotSetManagerProps {
 familyHash: string;
 isOwner: boolean;
}

export const SlotSetManager: React.FC<SlotSetManagerProps> = ({ familyHash, isOwner }) => {
 const [slotSets, setSlotSets] = useState<SlotSet[]>([]);
 const [activeSet, setActiveSet] = useState<string>('A');
 // Initialize selectedSet from store's editingSlotSet
 const [selectedSet, setSelectedSet] = useState<string>(moFamilyStore.getState().editingSlotSet || 'A');
 const [isLoading, setIsLoading] = useState(false);
 const [showCreateModal, setShowCreateModal] = useState(false);
 const [newSetName, setNewSetName] = useState('');
 const [copyFromSet, setCopyFromSet] = useState<string>('');
 
 useEffect(() => {
 loadSlotSets();
 // Sync with store's editingSlotSet on mount
 const currentEditingSet = moFamilyStore.getState().editingSlotSet;
 if (currentEditingSet && currentEditingSet !== selectedSet) {
 setSelectedSet(currentEditingSet);
 }
 
 // Subscribe to store updates
 const unsubscribe = moFamilyStore.subscribe((state) => {
 const sets = state.slotSets.get(familyHash);
 if (sets) {
 setSlotSets(sets);
 const active = sets.find(s => s.isActive);
 if (active) {
 setActiveSet(active.setName);
 }
 }
 
 // Also sync with editingSlotSet changes
 if (state.editingSlotSet && state.editingSlotSet !== selectedSet) {
 setSelectedSet(state.editingSlotSet);
 }
 });
 
 return () => unsubscribe();
 }, [familyHash]);
 
 const loadSlotSets = async () => {
 setIsLoading(true);
 try {
 const response = await fetch(`/mofamily/${familyHash}/slot-sets`, {
 credentials: 'include'
 });
 
 if (response.ok) {
 const data = await response.json();
 moFamilyStore.getState().setSlotSets(familyHash, data.data.sets);
 setSlotSets(data.data.sets);
 setActiveSet(data.data.activeSet);
 } else {
 throw new Error(`Failed to load slot sets: ${response.status}`);
 }
 } catch (error) {
 console.error('Error loading slot sets:', error);
 } finally {
 setIsLoading(false);
 }
 };
 
 const handleSelectSet = (setName: string) => {
 if (!isOwner) return;
 setSelectedSet(setName);
 // Update the store's editingSlotSet
 moFamilyStore.getState().setEditingSlotSet(setName);
 };
 
 const handleActivateSet = async (setName: string) => {
 if (!isOwner || setName === activeSet) return;
 
 try {
 await moFamilyStore.getState().switchActiveSlotSet(familyHash, setName);
 setActiveSet(setName);
 } catch (error) {
 console.error('Error switching slot set:', error);
 }
 };
 
 const handleCreateSet = async () => {
 if (!newSetName.trim()) return;
 
 try {
 await moFamilyStore.getState().createSlotSet(
 familyHash, 
 newSetName.trim(),
 copyFromSet || undefined
 );
 setShowCreateModal(false);
 setNewSetName('');
 setCopyFromSet('');
 } catch (error) {
 console.error('Error creating slot set:', error);
 }
 };
 
 const handleDeleteSet = async (setName: string) => {
 if (!isOwner || slotSets.length <= 2) return;
 
 if (!confirm(`정말로 슬롯 세트 "${setName}"를 삭제하시겠습니까?`)) return;
 
 try {
 const response = await fetch(`/mofamily/${familyHash}/slot-sets/${setName}`, {
 method: 'DELETE',
 credentials: 'include'
 });
 
 if (response.ok) {
 await loadSlotSets();
 } else {
 const error = await response.json();
 alert(error.error || '슬롯 세트 삭제에 실패했습니다.');
 }
 } catch (error) {
 console.error('Error deleting slot set:', error);
 }
 };
 
 if (isLoading) {
 return <div className="text-center py-4">슬롯 세트 로딩 중...</div>;
 }
 
 return (
 <div className="mt-4">
 <div className="flex items-center justify-between mb-4">
 <h3 className="text-lg font-semibold">
 슬롯 세트 관리
 {selectedSet && (
 <span className="text-sm text-purple-600 ml-2">(세트 {selectedSet} 편집 중)</span>
 )}
 </h3>
 <div className="flex items-center gap-3">
 {isOwner && selectedSet && (
 <div className="flex gap-2">
 <button
 onClick={async () => {
 try {
 const response = await fetch(`/mofamily/${familyHash}/slot-sets/${selectedSet}/slots`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({})
 });
 
 if (response.ok) {
 await loadSlotSets();
 
 // Show success notification
 const notification = document.createElement('div');
 notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 shadow-lg z-50';
 notification.textContent = `세트 ${selectedSet}에 슬롯이 추가되었습니다.`;
 document.body.appendChild(notification);
 setTimeout(() => notification.remove(), 3000);
 } else {
 const error = await response.json();
 alert(error.error || '슬롯 추가에 실패했습니다.');
 }
 } catch (error) {
 console.error('Error adding slot:', error);
 alert('슬롯 추가 중 오류가 발생했습니다.');
 }
 }}
 className="px-3 py-1 bg-green-500 text-white text-sm hover:bg-green-600"
 >
 + 슬롯
 </button>
 {(() => {
 const currentSet = slotSets.find(s => s.setName === selectedSet);
 return currentSet && currentSet.slots.length > 1 && (
 <button
 onClick={async () => {
 const lastSlot = currentSet.slots[currentSet.slots.length - 1];
 if (!lastSlot) return;
 
 if (!confirm(`세트 ${selectedSet}에서 마지막 슬롯을 제거하시겠습니까?`)) return;
 
 try {
 const response = await fetch(
 `/mofamily/${familyHash}/slot-sets/${selectedSet}/slots/${lastSlot.slot_number}`, 
 {
 method: 'DELETE',
 credentials: 'include'
 }
 );
 
 if (response.ok) {
 await loadSlotSets();
 
 // Show success notification
 const notification = document.createElement('div');
 notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 shadow-lg z-50';
 notification.textContent = `세트 ${selectedSet}에서 슬롯이 제거되었습니다.`;
 document.body.appendChild(notification);
 setTimeout(() => notification.remove(), 3000);
 } else {
 const error = await response.json();
 alert(error.error || '슬롯 제거에 실패했습니다.');
 }
 } catch (error) {
 console.error('Error removing slot:', error);
 alert('슬롯 제거 중 오류가 발생했습니다.');
 }
 }}
 className="px-3 py-1 bg-orange-500 text-white text-sm hover:bg-orange-600"
 >
 - 슬롯
 </button>
 );
 })()}
 </div>
 )}
 {isOwner && (
 <button
 onClick={() => setShowCreateModal(true)}
 className="px-3 py-1 bg-blue-500 text-white hover:bg-blue-600"
 >
 새 세트 추가
 </button>
 )}
 </div>
 </div>
 
 <div className="space-y-2">
 {slotSets.map(set => (
 <div
 key={set.id}
 className={`p-3 border ${
 selectedSet === set.setName ? 'border-purple-500 bg-purple-50' : 'border-gray-300'
 } ${set.isActive ? 'ring-2 ring-blue-300' : ''}`}
 >
 <div className="flex items-center justify-between">
 <div 
 className={`flex items-center gap-3 flex-1 ${isOwner ? 'cursor-pointer' : ''}`}
 onClick={isOwner ? () => handleSelectSet(set.setName) : undefined}
 >
 <span className="font-medium">세트 {set.setName}</span>
 {set.isActive && (
 <>
 <span className="px-2 py-1 bg-blue-500 text-white text-xs">
 활성
 </span>
 <span className="relative inline-block group">
 <span className="text-blue-500 cursor-help text-xs">?</span>
 <div className="hidden group-hover:block absolute bottom-full left-0 w-64 p-3 mb-2 text-xs bg-gray-800 text-white z-10 whitespace-pre-wrap">
 활성화 되어 있는 슬롯 목록을 편집하면, 편집 결과가 실시간으로 반영 됩니다. 완성된 슬롯 목록을 한 번에 적용하고 싶거나, 나중에 적용할 목록을 미리 편집해 두려면 비활성화 슬롯 목록을 편집하세요.
 <div className="absolute -bottom-2 left-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
 </div>
 </span>
 </>
 )}
 {selectedSet === set.setName && (
 <span className="px-2 py-1 bg-purple-500 text-white text-xs">
 편집 중
 </span>
 )}
 <span className="text-sm text-gray-600">
 {set.slots.length}개 슬롯
 </span>
 </div>
 
 {isOwner && (
 <div className="flex gap-2">
 {!set.isActive && (
 <button
 onClick={() => handleActivateSet(set.setName)}
 className="px-3 py-1 bg-blue-500 text-white text-sm hover:bg-blue-600"
 >
 활성화
 </button>
 )}
 {!set.isActive && slotSets.length > 2 && (
 <button
 onClick={() => handleDeleteSet(set.setName)}
 className="px-3 py-1 bg-red-500 text-white text-sm hover:bg-red-600"
 >
 삭제
 </button>
 )}
 </div>
 )}
 </div>
 </div>
 ))}
 </div>
 
 {/* Create Set Modal */}
 {showCreateModal && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
 <div className="bg-white p-6 w-96">
 <h3 className="text-lg font-semibold mb-4">새 슬롯 세트 만들기</h3>
 
 <div className="mb-4">
 <label className="block text-sm font-medium mb-1">
 세트 이름
 </label>
 <input
 type="text"
 value={newSetName}
 onChange={(e) => setNewSetName(e.target.value)}
 className="w-full px-3 py-2 border"
 placeholder="예: C, D, 백업..."
 maxLength={50}
 />
 </div>
 
 <div className="mb-4">
 <label className="block text-sm font-medium mb-1">
 복사할 세트 (선택사항)
 </label>
 <select
 value={copyFromSet}
 onChange={(e) => setCopyFromSet(e.target.value)}
 className="w-full px-3 py-2 border"
 >
 <option value="">새로 시작</option>
 {slotSets.map(set => (
 <option key={set.id} value={set.setName}>
 세트 {set.setName} 복사
 </option>
 ))}
 </select>
 </div>
 
 <div className="flex justify-end gap-2">
 <button
 onClick={() => {
 setShowCreateModal(false);
 setNewSetName('');
 setCopyFromSet('');
 }}
 className="px-4 py-2 border hover:bg-gray-100"
 >
 취소
 </button>
 <button
 onClick={handleCreateSet}
 disabled={!newSetName.trim()}
 className="px-4 py-2 bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
 >
 만들기
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
};